import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Activity, 
  TrendingUp, 
  Apple, 
  BookOpen, 
  Leaf,
  Heart,
  FileText,
  BookMarked,
  ArrowRight,
  Check,
  Rss,
  ExternalLink,
} from 'lucide-react';
import Seo from '../components/Seo';

const LandingPage = () => {
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`)
      .then(res => res.ok ? res.json() : null)
      .then(data => setPricing(data))
      .catch(() => {});
  }, []);

  const navigate = useNavigate();
  const { user } = useAuth();

  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const features = [
    {
      icon: TrendingUp,
      title: 'Track Blood Results',
      description: 'Log and visualise key blood markers over time. See trends clearly, not just numbers. Keep everything in one place.',
      detail: 'Understand your blood results in context, over time.'
    },
    {
      icon: Apple,
      title: 'Integrative Health Education',
      description: 'Explore food, lifestyle, and wellbeing ideas commonly discussed in integrative health — presented clearly and responsibly.',
      detail: 'Food profiles, supplements, and lifestyle practices. Educational only.'
    },
    {
      icon: BookOpen,
      title: 'Recipes & Food Literacy',
      description: 'Practical, gentle recipes that help you apply food knowledge in everyday life — without plans, protocols, or pressure.',
      detail: 'Simple, whole-food recipes chosen for supportive eating patterns.'
    },
    {
      icon: BookMarked,
      title: 'Learn & Notes (Personal Reflection)',
      description: 'Capture questions, insights, and reflections as you learn — and keep them linked to what matters to you.',
      detail: 'Your private space to make meaning as you explore.'
    },
    {
      icon: FileText,
      title: 'Exports & Sharing',
      description: 'Create clean summaries you can keep for yourself or bring to appointments.',
      detail: 'Clinical-grade PDF exports with charts and context.'
    },
    {
      icon: Heart,
      title: 'Track Symptoms & Medications',
      description: 'Log daily symptoms, medications, and vitamins. Build a complete picture of your health journey.',
      detail: 'Everything in one secure, private place.'
    }
  ];

  const whoThisIsFor = [
    'People living with CLL (Chronic Lymphocytic Leukaemia)',
    'Also suitable for SLL (Small Lymphocytic Lymphoma)',
    'People on watch & wait who want clarity',
    'People who value integrative wellbeing alongside medical care'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="MyCBC – CLL Blood Cancer Companion | Track Blood Results & Symptoms"
        description="Free app for Chronic Lymphocytic Leukemia (CLL) and Small Lymphocytic Lymphoma (SLL) patients. Track blood results, WBC, lymphocytes, symptoms, and treatments. Free full access for 30 days."
        path="/"
      />
      {/* Top Navigation Bar */}
      <nav className="absolute top-0 left-0 right-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            <span className="text-base sm:text-lg font-bold text-white">
              <span className="sm:hidden">MyCBC</span>
              <span className="hidden sm:inline">Blood Cancer Companion</span>
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => navigate('/blog')}
              className="px-3 sm:px-4 py-2 text-white/90 hover:text-white transition-colors font-medium text-sm"
            >
              Blog
            </button>
            <button
              onClick={() => navigate('/login')}
              className="px-3 sm:px-4 py-2 bg-white/20 backdrop-blur-sm text-white border border-white/30 rounded-lg hover:bg-white/30 transition-all font-medium text-sm whitespace-nowrap"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      <main>
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-500 via-blue-400 to-teal-400">
        <div className="max-w-6xl mx-auto px-6 pt-20 pb-24 md:pt-32 md:pb-32">
          <div className="text-center">
            {/* Icon */}
            <div className="flex justify-center mb-8">
              <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl shadow-lg">
                <Activity className="h-12 w-12 text-white" />
              </div>
            </div>
            
            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-tight drop-shadow-lg">
              A clearer way to live with CLL/SLL
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-white/95 leading-relaxed mb-10 max-w-3xl mx-auto drop-shadow">
              Track your health, explore integrative wellbeing, and build your own understanding — all in one private, secure place.
            </p>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <button
                onClick={() => navigate('/register')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-all font-semibold shadow-xl hover:shadow-2xl text-lg"
              >
                Start your private health companion
                <ArrowRight className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate('/preview')}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 rounded-xl hover:bg-white/20 transition-all font-medium text-lg"
              >
                Try Interactive Preview
              </button>
            </div>
            
            {/* Secondary CTA */}
            <button
              onClick={() => {
                document.getElementById('features').scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-white/90 hover:text-white text-sm font-medium underline underline-offset-4 mb-8"
            >
              Explore what's included ↓
            </button>

            {/* Trust statement */}
            <p className="text-sm text-white/90 max-w-2xl mx-auto">
              Your health journey is personal. Your tools should be too.
            </p>
          </div>
        </div>
      </div>

      {/* Key Benefits Section - Why Track */}
      <div className="py-16 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Track Your Health Data?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              When so much feels uncertain, seeing what's actually happening can make it easier to manage.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* See Patterns */}
            <div className="bg-white rounded-2xl p-6 border-2 border-teal-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-5">
                <TrendingUp className="h-7 w-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                See Your Patterns
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Our graphs make it easy to spot trends over time. When you start a new treatment, you'll be able to watch your blood markers respond — seeing how things unfold month by month in clear, visual charts. Long-term trends matter more than any single result. One blood test doesn't define your health.
              </p>
            </div>

            {/* Track Changes */}
            <div className="bg-white rounded-2xl p-6 border-2 border-blue-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-5">
                <Activity className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Track What's Changing
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Started a new medication? Added a vitamin to your routine? Track your blood results alongside your treatments and see whether they're making a difference. Replace guessing with data-backed understanding — and feel more certain about what you're seeing.
              </p>
            </div>

            {/* Connect the Dots */}
            <div className="bg-white rounded-2xl p-6 border-2 border-purple-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-5">
                <Leaf className="h-7 w-7 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">
                Connect the Dots
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Your medications, vitamins, and supplements all appear on your timeline. When your bloods change after starting something new, you'll see the connection clearly. Understanding cause and effect — even without certainty — can restore a sense of control and confidence in your own journey.
              </p>
            </div>
          </div>

          {/* Closing line */}
          <p className="text-center text-slate-500 text-base mt-10 max-w-2xl mx-auto">
            Clarity supports better conversations and more confident decisions — without replacing medical care.
          </p>
        </div>
      </div>

      {/* How to Use This Platform Section */}
      <div className="py-16 px-6 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 text-center">
            How it works
          </h2>
          
          {/* Intro */}
          <div className="text-center mb-12">
            <p className="text-lg leading-relaxed text-slate-700">
              MyCBC gives you the tools to track, understand, and manage<br />
              your health journey with CLL.
            </p>
          </div>

          {/* Usage Patterns */}
          <div className="space-y-10">
            {/* Block 1 - Track */}
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Track your blood results
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-700">
                Add your blood test results after each appointment. See your key markers charted over time, with clear trends showing how your health is progressing.
              </p>
            </div>

            {/* Block 2 - Manage */}
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Manage your treatments
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-700">
                Record your vitamins, supplements, and medications with start and end dates. See them overlaid on your blood result timeline to spot potential correlations.
              </p>
            </div>

            {/* Block 3 - Learn */}
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Learn and prepare
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-700">
                Explore educational content about CLL, nutrition, and integrative health. Use the notes section to write down questions for your next appointment.
              </p>
            </div>

            {/* Block 4 - Stay Updated */}
            <div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Catch up with the latest news
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-700">
                Stay informed with curated CLL/SLL news, research updates, and blog posts. Subscribe to get notified when new articles are published.
              </p>
            </div>
          </div>

          {/* Closing */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-lg md:text-xl leading-relaxed text-slate-800 text-center font-medium">
              Export a PDF summary to share with your healthcare team<br />
              for more informed conversations.
            </p>
          </div>
        </div>
      </div>

      {/* What This Platform Helps You Do */}
      <div id="features" className="py-20 px-6 bg-gradient-to-b from-white via-blue-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              What this platform helps you do
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Six core areas designed to support your understanding and reflection.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const iconColors = [
                { bg: 'bg-gradient-to-br from-red-500 to-pink-500', text: 'text-white' },
                { bg: 'bg-gradient-to-br from-teal-500 to-green-500', text: 'text-white' },
                { bg: 'bg-gradient-to-br from-orange-500 to-amber-500', text: 'text-white' },
                { bg: 'bg-gradient-to-br from-purple-500 to-pink-500', text: 'text-white' },
                { bg: 'bg-gradient-to-br from-blue-500 to-cyan-500', text: 'text-white' },
                { bg: 'bg-gradient-to-br from-indigo-500 to-purple-500', text: 'text-white' }
              ];
              const color = iconColors[index % iconColors.length];
              
              return (
                <div 
                  key={index}
                  className="bg-white rounded-2xl p-8 border border-slate-100 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`p-3 ${color.bg} rounded-xl flex-shrink-0 shadow-md`}>
                      <Icon className={`h-6 w-6 ${color.text}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 pt-2">
                      {feature.title}
                    </h3>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-3">
                    {feature.description}
                  </p>
                  <p className="text-sm text-slate-500 italic">
                    {feature.detail}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Latest CLL/SLL News Section */}
      <div className="py-16 px-6 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-4">
              <Rss className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-700">Stay Informed</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Latest CLL/SLL News
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Curated news from trusted sources, delivered to your dashboard
            </p>
            <a
              href="/register"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-slate-700 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors font-medium"
            >
              Browse All CLL News
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Who This Is For */}
      <div className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Who this is for
            </h2>
          </div>
          
          <div className="bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl p-8 border border-blue-200 shadow-lg">
            <ul className="space-y-4">
              {whoThisIsFor.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="p-1 bg-teal-500 rounded-full flex-shrink-0 mt-0.5">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-lg text-slate-800 font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="plans" className="py-20 px-6 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">
            Free for your first 30 days
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Simple pricing
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto">
            Every new account unlocks the whole site free for 30 days — no credit card required. After 30 days you simply roll back to the free version, or upgrade for full access from USD $2.50 a week.
          </p>

          {/* Price cards */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8 pt-3">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 text-left">
              <p className="text-sm font-semibold text-slate-500 mb-1">Weekly</p>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-900">$2.50</span>
                <span className="text-slate-600">USD /week</span>
              </div>
              {pricing?.weekly_aud && (
                <p className="text-xs text-slate-400 mt-1" data-testid="weekly-aud-price">
                  ≈ A${pricing.weekly_aud.toFixed(2)} AUD
                </p>
              )}
              <p className="text-sm text-slate-500 mt-2">After your free 30 days. Cancel anytime.</p>
            </div>
            <div className="bg-white rounded-2xl border-2 border-teal-300 p-6 text-left relative overflow-hidden">
              <span className="absolute top-3 right-3 px-2.5 py-1 bg-teal-600 text-white text-xs font-semibold rounded-full" data-testid="yearly-save-badge">Save 24%</span>
              <p className="text-sm font-semibold text-teal-600 mb-1">Yearly</p>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-slate-900">$99</span>
                <span className="text-slate-600">USD /year</span>
              </div>
              {pricing?.yearly_aud && (
                <p className="text-xs text-slate-400 mt-1" data-testid="yearly-aud-price">
                  ≈ A${pricing.yearly_aud} AUD
                </p>
              )}
              <p className="text-sm text-slate-500 mt-2">Under USD $1.91 a week. After your free 30 days.</p>
            </div>
          </div>

          {/* Features */}
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 mb-8 border border-teal-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-6">Everything included:</h3>
            <div className="grid sm:grid-cols-2 gap-4 text-left max-w-lg mx-auto">
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Full blood results tracking</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Trend graphs with reference ranges</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Nutrition & deficiency tracking</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Medications & supplements</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Symptom tracking</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">PDF exports for your doctor</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">Integrative health content</span>
              </div>
              <div className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">All future updates included</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={() => navigate('/register')}
            className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700 text-white rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl text-lg"
          >
            Start your free 30 days
            <ArrowRight className="h-5 w-5" />
          </button>
          <p className="text-sm text-slate-500 mt-4">
            Free for 30 days • Rolls back to the free version after — no auto-charge • No credit card required
          </p>
        </div>
      </div>

      {/* Final CTA */}
      <div className="py-20 px-6 bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 drop-shadow-lg">
            Your health journey is personal.<br />Your tools should be too.
          </h2>
          <p className="text-lg text-white/95 mb-10 max-w-2xl mx-auto drop-shadow">
            Join people with CLL who are taking a clearer, more organised approach to understanding their health.
          </p>
          
          <button
            onClick={() => navigate('/register')}
            className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-all font-semibold shadow-2xl hover:shadow-xl hover:scale-105 text-lg"
          >
            Create your private account
            <ArrowRight className="h-5 w-5" />
          </button>

          <p className="text-sm text-white/90 mt-8">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-white font-semibold underline hover:no-underline"
            >
              Login here
            </button>
          </p>
        </div>
      </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <Activity className="h-6 w-6 text-blue-600" />
              <span className="font-semibold text-slate-900">Blood Cancer Companion</span>
            </div>
            
            <div className="flex gap-8 text-sm">
              <button
                onClick={() => navigate('/blog')}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                Blog
              </button>
              <button
                onClick={() => navigate('/terms')}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                Terms of Service
              </button>
              <button
                onClick={() => navigate('/privacy')}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                Privacy Policy
              </button>
            </div>
          </div>
          
          {/* Medical Disclaimer */}
          <div className="text-center mt-6 pt-6 border-t border-slate-100">
            <p className="text-xs text-slate-500 max-w-2xl mx-auto">
              This platform is for personal health tracking and educational purposes only. It does not provide medical advice, diagnosis, or treatment. Always consult your healthcare provider for medical decisions.
            </p>
          </div>
          
          <div className="text-center mt-4">
            <p className="text-slate-500 text-sm">
              &copy; 2025 Blood Cancer Companion
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
